import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Message } from "primereact/message";

import SettingChangeHistory from "../AppUsers/notifications/SettingChangeHistory";
import UserLookup from "./UserLookup";
import UserHeading from "./UserHeading";
import { resolveToEmaid } from "./useResolvedUser";

const SettingAuditPage = () => {
  const { emaid } = useParams();
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  /** Accepts an email as well as an eMAID; the audit is keyed by the latter. */
  const lookup = async (entered: string) => {
    setLookupError(null);
    setResolving(true);
    const { emaid: resolved, error } = await resolveToEmaid(entered);
    setResolving(false);
    if (resolved) navigate(`/notifications/audit/${encodeURIComponent(resolved)}`);
    else setLookupError(error);
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Notification setting changes</div>
          <p className="text-color-secondary mt-2 mb-0">
            Who changed a person's preferences or subscriptions, and when — staff, the person
            themselves, or an unsubscribe link.
          </p>
        </div>
        {emaid && (
          <Button
            label="Subscriptions"
            icon="pi pi-bell"
            outlined
            onClick={() => navigate(`/notifications/subscriptions/${encodeURIComponent(emaid)}`)}
          />
        )}
      </div>

      <div className="mb-4">
        <UserLookup
          value={emaid ?? ""}
          onSubmit={lookup}
          resolving={resolving}
          error={lookupError}
          hint="An email is resolved to the eMAID the trail is keyed by."
        />
      </div>

      {emaid ? (
        <Card className="shadow-1" title={<UserHeading emaid={emaid} />}>
          <SettingChangeHistory userId={emaid} />
        </Card>
      ) : (
        <div data-testid="no-user-chosen">
          <Message severity="info" text="Enter an eMAID or email above to see who changed that person's settings." />
        </div>
      )}
    </div>
  );
};

export default SettingAuditPage;
